import {useParams, useNavigate} from "react-router-dom";
import {Tag} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon, Loading, PageHeader, ProjectToolNav} from "~/components";
import {useCan} from "~/hooks";
import {useGetProjectQuery} from "~/reduxs/api/projectApiSlice";
import ProjectOverview from "../components/ProjectOverview";
import ProjectChecklist from "../components/ProjectChecklist";
import style from "../style/Project.module.scss";

const cn = className.bind(style);

/**
 * Dashboard của một Dự án: thông tin website + lưới CÔNG CỤ SEO bên trong dự án.
 * Site Crawler đang hoạt động; các tool khác (Google Search Console...) hiển thị "Sắp có".
 */
function ProjectDashboard() {

	const {id} = useParams();
	const navigate = useNavigate();
	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');
	const canAi = useCan('ai');

	const {data: project, isLoading} = useGetProjectQuery(id);

	if (isLoading) return <Loading />;
	if (!project) return <div className="container">Không tìm thấy dự án.</div>;

	// Danh sách công cụ của dự án (mở rộng dần khi thêm tool mới).
	const tools = [
		{
			key: 'crawler',
			name: 'Site Crawler',
			desc: 'Crawl toàn site, chấm điểm SEO từng trang + phát hiện lỗi cấp site (duplicate, broken link, orphan...).',
			icon: 'fa-light fa-spider',
			color: '#4f46e5',
			available: true,
			allowed: canCrawl,
			to: `/projects/${project.id}/crawler`,
		},
		{
			key: 'technical',
			name: 'Kỹ thuật SEO',
			desc: 'Báo cáo kỹ thuật từ lần crawl mới nhất: liên kết nội bộ & cơ hội liên kết, liên kết hỏng, chuyển hướng, robots.txt.',
			icon: 'fa-light fa-screwdriver-wrench',
			color: '#0891b2',
			available: true,
			allowed: canCrawl,
			to: `/projects/${project.id}/technical`,
		},
		{
			key: 'gsc',
			name: 'Google Search Console',
			desc: 'Kết nối GSC để theo dõi hiệu suất tìm kiếm, click/impression, từ khoá, lập chỉ mục.',
			icon: 'fa-light fa-magnifying-glass-chart',
			color: '#0ea5e9',
			available: true,
			allowed: canGsc,
			to: `/projects/${project.id}/gsc`,
		},
		{
			key: 'ai',
			name: 'AI SEO',
			desc: 'Trợ lý AI phân tích website dựa trên dữ liệu crawl/SEO/GSC + sinh nội dung (meta, FAQ, dàn ý).',
			icon: 'fa-light fa-wand-magic-sparkles',
			color: '#7c3aed',
			available: true,
			allowed: canAi,
			to: `/projects/${project.id}/ai`,
		},
	];

	return (
		<div className="container">
			<ProjectToolNav projectId={project.id} active="overview" />

			<PageHeader
				icon="fa-light fa-folder-open"
				title={project.name}
				subtitle={project.root_url}
			/>

			<div className={cn('card')}>
				<div className={cn('infoRow')}>
					<div>Website: <strong>{project.root_url}</strong></div>
					<div>Domain: <strong>{project.domain}</strong></div>
				</div>
			</div>

			{/* Onboarding: hướng dẫn bước tiếp theo, tự ẩn khi đã dùng đủ các tool. */}
			<ProjectChecklist projectId={project.id} />

			{/* Tổng quan: gom số liệu từ các tool đã chạy (crawler/gsc/pagespeed/seo). */}
			<ProjectOverview projectId={project.id} />

			<h3 style={{fontSize: 15, fontWeight: 600, margin: '20px 0 10px'}}>Công cụ SEO</h3>
			<div className={cn('tools')}>
				{tools.map((t) => {
					const clickable = t.available && t.allowed;
					return (
						<div
							key={t.key}
							className={cn('toolCard', {active: clickable, disabled: !clickable})}
							onClick={() => clickable && navigate(t.to)}
						>
							<div className={cn('toolIcon')} style={{background: t.color}}>
								<FontAwesomeIcon icon={t.icon} />
							</div>
							<div className={cn('cardHead')} style={{alignItems: 'center'}}>
								<h4 className={cn('toolName')}>{t.name}</h4>
								{!t.available && <Tag>Sắp có</Tag>}
								{t.available && !t.allowed && <Tag color="red">Không có quyền</Tag>}
							</div>
							<div className={cn('toolDesc')}>{t.desc}</div>
							{clickable && (
								<div style={{color: t.color, fontSize: 13, fontWeight: 500}}>
									Mở công cụ <FontAwesomeIcon icon="fa-light fa-arrow-right" />
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default ProjectDashboard;
